<script lang="ts">
/**
 * The app's icon set, inline so the glyphs cannot vary across platforms.
 *
 * Text characters (⏸ ▶ ⏹ ←) render at different weights and optical sizes
 * depending on the font a device falls back to, and sit off-centre in a round
 * button. These are drawn to a shared 24px grid and inherit `currentColor`.
 */
export type IconName = 'play' | 'pause' | 'stop' | 'settings' | 'back' | 'refresh' | 'delete'
</script>

<script setup lang="ts">
withDefaults(
  defineProps<{
    name: IconName
    /** Edge length in px; the viewBox is always 24. */
    size?: number
  }>(),
  { size: 26 },
)
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    class="icon"
  >
    <!-- A right-pointing triangle's visual mass is its centroid, one third
         from the base — not its bounding box. Drawn so that centroid lands at
         x=12.7, just right of the grid centre, which is what reads as centred
         inside a round button. -->
    <path
      v-if="name === 'play'"
      fill="currentColor"
      d="M9.6 5.8v12.4a1 1 0 0 0 1.54.84l9.3-6.2a1 1 0 0 0 0-1.68l-9.3-6.2A1 1 0 0 0 9.6 5.8Z"
    />

    <g v-else-if="name === 'pause'" fill="currentColor">
      <rect x="7" y="5" width="3.6" height="14" rx="1.4" />
      <rect x="13.4" y="5" width="3.6" height="14" rx="1.4" />
    </g>

    <rect v-else-if="name === 'stop'" x="6.5" y="6.5" width="11" height="11" rx="2.4" fill="currentColor" />

    <path
      v-else-if="name === 'refresh'"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M20 12a8 8 0 1 1-2.6-5.9M20 4.5V10h-5.5"
    />

    <g
      v-else-if="name === 'delete'"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M4.5 6.5h15M9.5 6.5V4.8a1.3 1.3 0 0 1 1.3-1.3h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" />
      <path d="M6.8 6.5 7.7 19a1.5 1.5 0 0 0 1.5 1.4h5.6a1.5 1.5 0 0 0 1.5-1.4l.9-12.5" />
      <path d="M10.5 10v6.5M13.5 10v6.5" />
    </g>

    <path
      v-else-if="name === 'back'"
      fill="none"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M15 5 8 12l7 7"
    />

    <g
      v-else
      fill="none"
      stroke="currentColor"
      stroke-width="1.9"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <circle cx="12" cy="12" r="3.4" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.9 19.3a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.53 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.7 8.9a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.53h.08A1.7 1.7 0 0 0 10.1 2.97V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.88v.08a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51 1.03Z"
      />
    </g>
  </svg>
</template>

<style scoped>
.icon {
  display: block;
  flex: 0 0 auto;
}
</style>
